import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RetentionOverviewCards from './components/RetentionOverviewCards';
import AtRiskMembersList from './components/AtRiskMembersList';
import RetentionStrategiesPanel from './components/RetentionStrategiesPanel';
import EngagementMetricsChart from './components/EngagementMetricsChart';
import AIRetentionInsights from './components/AIRetentionInsights';
import { openaiService } from '../../lib/openaiService';

const MemberRetention = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [error, setError] = useState(null);

  const userName = userProfile?.full_name || 'User';
  const userRole = userProfile?.role || 'manager';

  // Mock retention data - replace with Supabase queries in production
  const retentionData = {
    totalMembers: 1247,
    activeMembers: 1089,
    atRiskCount: 52,
    churnRate: 4.2,
    avgEngagementScore: 7.8,
    retentionRate: 95.8,
    members: [
      {
        id: '1',
        name: 'Sarah Johnson',
        lastVisit: '2026-01-18',
        visitFrequency: 2.1,
        engagementScore: 3.2,
        membershipDuration: 14,
        preferredActivities: ['Yoga', 'Cardio']
      },
      {
        id: '2',
        name: 'Michael Chen',
        lastVisit: '2026-01-20',
        visitFrequency: 1.8,
        engagementScore: 4.1,
        membershipDuration: 8,
        preferredActivities: ['Weight Training', 'HIIT']
      },
      {
        id: '3',
        name: 'Emily Rodriguez',
        lastVisit: '2026-01-15',
        visitFrequency: 1.5,
        engagementScore: 3.8,
        membershipDuration: 22,
        preferredActivities: ['Swimming', 'Pilates']
      }
    ],
    engagementTrends: [
      { month: 'Aug', avgScore: 7.2, atRisk: 58 },
      { month: 'Sep', avgScore: 7.5, atRisk: 54 },
      { month: 'Oct', avgScore: 7.6, atRisk: 51 },
      { month: 'Nov', avgScore: 7.7, atRisk: 49 },
      { month: 'Dec', avgScore: 7.9, atRisk: 46 },
      { month: 'Jan', avgScore: 7.8, atRisk: 52 }
    ]
  };

  const generateAIInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const insights = await openaiService?.generateRetentionStrategies(retentionData);
      setAiInsights(insights);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-generate insights on mount
    generateAIInsights();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>
      <main className="container mx-auto px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              Member Retention Portal
            </h1>
            <p className="text-muted-foreground">
              AI-powered insights and strategies to improve member engagement and reduce churn
            </p>
          </div>
          <Button
            onClick={generateAIInsights}
            disabled={loading}
            iconName="Sparkles"
            iconPosition="left"
          >
            {loading ? 'Generating Insights...' : 'Refresh AI Insights'}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card border-l-4 border-l-error mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={24} color="var(--color-error)" />
              <div>
                <h3 className="font-semibold text-foreground mb-1">Error Loading AI Insights</h3>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Overview Cards */}
        <RetentionOverviewCards data={retentionData} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Column - Charts and Metrics */}
          <div className="lg:col-span-2 space-y-6">
            <EngagementMetricsChart data={retentionData?.engagementTrends} />
            <AtRiskMembersList members={aiInsights?.atRiskMembers || []} loading={loading} />
          </div>

          {/* Right Column - AI Insights */}
          <div className="space-y-6">
            <AIRetentionInsights insights={aiInsights} loading={loading} />
          </div>
        </div>

        {/* Retention Strategies Section */}
        <RetentionStrategiesPanel strategies={aiInsights?.retentionStrategies || []} loading={loading} />
      </main>
    </div>
  );
};

export default MemberRetention;